import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import Layout from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { StatsSkeleton } from './components/Skeleton';
import { ProfileGate, ProtectedRoute } from './components/auth';
import { FeatureGate } from './components/FeatureGate';

// New Linescore pages
const HomePage = lazy(() => import('./pages/HomePage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ScorePage = lazy(() => import('./pages/ScorePage'));
const Scoring101Page = lazy(() => import('./pages/Scoring101Page'));

// Game pages
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const ScorecardPrepPage = lazy(() => import('./pages/ScorecardPrepPage'));
const BoxScorePage = lazy(() => import('./pages/BoxScorePage'));
const GameScorecardsPage = lazy(() => import('./pages/GameScorecardsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

// Scorecard pages
const MyScorecards = lazy(() => import('./pages/scorecards/MyScorecards'));
const ArchivePage = lazy(() => import('./pages/scorecards/ArchivePage'));
const ScorecardStats = lazy(() => import('./pages/scorecards/ScorecardStats'));
const UserScorecardProfile = lazy(() => import('./pages/scorecards/UserScorecardProfile'));
const ScorecardDetailPage = lazy(() => import('./pages/scorecards/ScorecardDetailPage'));
// FollowingFeed and LikedScorecards are now embedded in MyScorecards tabs
const ScorecardSocialPage = lazy(() => import('./pages/scorecards/ScorecardSocialPage'));
const EditScorecardPage = lazy(() => import('./pages/scorecards/EditScorecardPage'));
const BulkUploadPage = lazy(() => import('./pages/scorecards/BulkUploadPage'));
const UserBadgesPage = lazy(() => import('./pages/scorecards/UserBadgesPage'));

// Profile pages
const ScoringDefaultsPage = lazy(() => import('./pages/profile/ScoringDefaults'));

// Legal & contact pages
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const CookiesPage = lazy(() => import('./pages/legal/CookiesPage'));
const ContactFormPage = lazy(() => import('./pages/ContactFormPage'));
const AcceptableUsePage = lazy(() => import('./pages/legal/AcceptableUsePage'));
const TipJarPage = lazy(() => import('./pages/TipJarPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

// Admin pages
const AdminLandingPage = lazy(() => import('./pages/admin/AdminLandingPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const HealthDashboardPage = lazy(() => import('./pages/admin/HealthDashboardPage'));
const ApiDocsPage = lazy(() => import('./pages/admin/ApiDocsPage'));
const FeatureFlagsAdmin = lazy(() => import('./pages/admin/FeatureFlagsAdmin'));
const ReactionTypesAdmin = lazy(() => import('./pages/admin/ReactionTypesAdmin'));
const AnalyticsDashboard = lazy(() => import('./pages/admin/AnalyticsDashboard'));
const FlaggedComments = lazy(() => import('./pages/admin/FlaggedComments'));
const FeaturedScorecards = lazy(() => import('./pages/admin/FeaturedScorecards'));
const FeedbackAdmin = lazy(() => import('./pages/admin/FeedbackAdmin'));

// Admin — Pressbox-origin components (formerly tabs inside PressboxAdmin)
const ModerationQueue = lazy(() => import('./components/admin/ModerationQueue'));
const EditorPickTab = lazy(() => import('./components/admin/EditorPickTab'));
const ArchiveSpotlightTab = lazy(() => import('./components/admin/ArchiveSpotlightTab'));
const MomentsTab = lazy(() => import('./components/admin/MomentsTab'));
const MilestonesTab = lazy(() => import('./components/admin/MilestonesTab'));
const BadgesTab = lazy(() => import('./components/admin/BadgesTab'));

// Global error fallback for the entire app
const GlobalErrorFallback = (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-lg text-center">
      <h2 className="text-xl font-bold text-red-800 mb-2">Something went wrong</h2>
      <p className="text-red-600 mb-4">An unexpected error occurred. Please refresh the page.</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

// Loading fallback for lazy-loaded pages
const PageLoadingFallback = (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
    <div className="max-w-7xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <StatsSkeleton />
      </div>
    </div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary fallback={GlobalErrorFallback}>
      <BrowserRouter>
        <ScrollToTop />
        <ProfileGate>
          <Suspense fallback={PageLoadingFallback}>
            <Routes>
              {/* Social permalink — standalone, public, own nav */}
              <Route path="/scorecards/:scorecardId/page" element={<ScorecardSocialPage />} />
              {/* Onboarding - standalone, no Layout wrapper */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                {/* Score page — download printable scorecards */}
                <Route
                  path="/score"
                  element={
                    <FeatureGate flag="show_score_page" fallback={<Navigate to="/" replace />}>
                      <ScorePage />
                    </FeatureGate>
                  }
                />
                {/* Scoring 101 — beginner-friendly primer on baseball scoring systems */}
                <Route
                  path="/scoring-101"
                  element={
                    <FeatureGate flag="show_scoring_101" fallback={<Navigate to="/" replace />}>
                      <Scoring101Page />
                    </FeatureGate>
                  }
                />
                {/* Gameday routes */}
                <Route
                  path="/gameday"
                  element={
                    <FeatureGate flag="show_todays_games" fallback={<Navigate to="/" replace />}>
                      <SchedulePage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="/gameday/preview/:gamePk"
                  element={
                    <FeatureGate flag="show_games_page" fallback={<Navigate to="/" replace />}>
                      <ScorecardPrepPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="/boxscore/:gamePk"
                  element={
                    <FeatureGate flag="show_games_page" fallback={<Navigate to="/" replace />}>
                      <BoxScorePage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="/boxscore/:gamePk/scorecards"
                  element={
                    <FeatureGate flag="show_games_page" fallback={<Navigate to="/" replace />}>
                      <GameScorecardsPage />
                    </FeatureGate>
                  }
                />
                {/* Scorecard pages */}
                <Route
                  path="/scorecards/my"
                  element={<Navigate to="/scorecards/my/stats" replace />}
                />
                <Route path="/scorecards/my/:tab" element={<MyScorecards />} />
                <Route path="/scorecards" element={<ArchivePage />} />
                <Route path="/scorecards/latest" element={<Navigate to="/scorecards" replace />} />
                <Route
                  path="/scorecards/stats"
                  element={
                    <FeatureGate flag="show_scorecard_stats" fallback={<Navigate to="/" replace />}>
                      <ScorecardStats />
                    </FeatureGate>
                  }
                />
                <Route
                  path="/scorecards/user/:username/badges"
                  element={
                    <FeatureGate flag="show_badges" fallback={<Navigate to="/" replace />}>
                      <UserBadgesPage />
                    </FeatureGate>
                  }
                />
                <Route path="/scorecards/user/:username" element={<UserScorecardProfile />} />
                <Route
                  path="/scorecards/bulk-upload"
                  element={
                    <FeatureGate
                      flag="show_bulk_upload"
                      fallback={<Navigate to="/scorecards" replace />}
                    >
                      <ProtectedRoute>
                        <BulkUploadPage />
                      </ProtectedRoute>
                    </FeatureGate>
                  }
                />
                <Route
                  path="/scorecards/:scorecardId/edit"
                  element={
                    <ProtectedRoute>
                      <EditScorecardPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/scorecards/:scorecardId" element={<ScorecardDetailPage />} />
                {/* Legal & contact pages - public, no auth required */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/cookies" element={<CookiesPage />} />
                <Route path="/acceptable-use" element={<AcceptableUsePage />} />
                <Route path="/form" element={<ContactFormPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/tip-jar"
                  element={
                    <FeatureGate flag="show_tip_jar" fallback={<Navigate to="/" replace />}>
                      <TipJarPage />
                    </FeatureGate>
                  }
                />

                {/* Following feed - redirect to My Scorebook tab */}
                <Route
                  path="/following"
                  element={<Navigate to="/scorecards/my/following" replace />}
                />
                {/* Protected routes - require authentication */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile/liked-scorecards"
                  element={<Navigate to="/scorecards/my/liked" replace />}
                />
                <Route
                  path="/profile/scoring-defaults"
                  element={
                    <ProtectedRoute>
                      <ScoringDefaultsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Admin routes — all under shared AdminLayout with breadcrumb */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireModerator>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminLandingPage />} />
                  {/* Site admin */}
                  <Route path="health" element={<HealthDashboardPage />} />
                  <Route path="api-docs" element={<ApiDocsPage />} />
                  <Route path="feature-flags" element={<FeatureFlagsAdmin />} />
                  <Route
                    path="reactions"
                    element={
                      <ProtectedRoute requireAdmin>
                        <ReactionTypesAdmin />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="analytics"
                    element={
                      <ProtectedRoute requireSuperAdmin>
                        <AnalyticsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="feedback"
                    element={
                      <ProtectedRoute requireSuperAdmin>
                        <FeedbackAdmin />
                      </ProtectedRoute>
                    }
                  />
                  {/* Content curation (formerly Pressbox + standalone) */}
                  <Route path="moderation" element={<ModerationQueue />} />
                  <Route path="flagged-comments" element={<FlaggedComments />} />
                  <Route path="featured" element={<FeaturedScorecards />} />
                  <Route path="editor-pick" element={<EditorPickTab />} />
                  <Route path="spotlight" element={<ArchiveSpotlightTab />} />
                  <Route path="moments" element={<MomentsTab />} />
                  <Route path="milestones" element={<MilestonesTab />} />
                  <Route path="badges" element={<BadgesTab />} />
                </Route>
                {/* Removed pages (/team, /player, /managers, /transactions, ...)
                    and any other unknown URL go home rather than rendering a
                    blank page — old links to them are still out there. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </ProfileGate>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
